import PropTypes from "prop-types";
import { useRef, useState } from "react";
import moment from "moment";

// ICONS
import InProgressIcon from "@PayPlans/assets/icons/oneSpanStatus/InProgress.svg";
import DeclinedIcon from "@PayPlans/assets/icons/oneSpanStatus/Declined.svg";
import ExpiredIcon from "@PayPlans/assets/icons/oneSpanStatus/Expired.svg";
import CompletedIcon from "@PayPlans/assets/icons/oneSpanStatus/Completed.svg";
import DraftIcon from "@PayPlans/assets/icons/oneSpanStatus/Draft.svg";
import VoidedIcon from "@PayPlans/assets/icons/oneSpanStatus/Voided.svg";

// MUI
import { Box, Paper, Popover, Typography } from "@mui/material";

//COMPNENTS
import SendEmailButton from "@/Layouts/components/Buttons/SendEmailButton";
import clsx from "clsx";
import { DATE_FORMAT } from "@/Constants/date";

// Constants
const STATUS_INPROGRESS = "INPROGRESS";

const STATUS_MAP = new Map([
	[
		"COMPLETED",
		{
			icon: CompletedIcon,
			message: "Complete",
		},
	],
	[
		"INPROGRESS",
		{
			icon: InProgressIcon,
			message: "In Progress",
		},
	],
	[
		"DECLINED",
		{
			icon: DeclinedIcon,
			message: "Declined",
		},
	],
	[
		"EXPIRED",
		{
			icon: ExpiredIcon,
			message: "Expired",
		},
	],
	[
		"DRAFT",
		{
			icon: DraftIcon,
			message: "Draft",
		},
	],
	[
		"VOIDED",
		{
			icon: VoidedIcon,
			message: "Voided",
		},
	],
]);

//Functions
function inProgressWithData(status, data, handleFunction) {
	return status === STATUS_INPROGRESS && data ? handleFunction : null;
}

const MessagePopover = (props) => {
	const { id = null, anchorEl = null, open = false, data = null, handleClose = () => {} } = props;
	// Filter the acknowledgementStatus array to only include items with status "INPROGRESS"
	const inProgressStatuses = data?.upcomingPayPlanAssignment?.acknowledgementStatus?.filter((item) => item.status === STATUS_INPROGRESS);

	// Sort the inProgressStatuses array by statusDate in descending order
	inProgressStatuses?.sort((a, b) => new Date(b.statusDate) - new Date(a.statusDate));

	// Get the latest inProgressStatus
	const latestInProgressStatus = inProgressStatuses?.[0];

	return (
		<div>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
			>
				<Typography sx={{ padding: "10px" }} className={"regular-pt-sans-pro-narrow-label fs-15"}>
					{latestInProgressStatus?.signingProgress?.length > 0 ? (
						latestInProgressStatus.signingProgress.map((item) => (
							<div key={item.roleId}>
								<p className="pay-plan-assignment-signer-progress-entry">
									<strong>{item.fullName}</strong>:
									<br />
									{item.signedAt ? (
										<>
											<strong>Signed:</strong>{" "}
											<span>{moment(item.signedAt).format(DATE_FORMAT.abbrDateOrdinalTime)}</span>
										</>
									) : (
										"Not signed"
									)}
								</p>
							</div>
						))
					) : (
						<p className="pay-plan-assignment-signer-progress-entry">Signing progress is not available.</p>
					)}
				</Typography>
			</Popover>
		</div>
	);
};
MessagePopover.propTypes = {
	id: PropTypes.string,
	open: PropTypes.bool.isRequired,
	anchorEl: PropTypes.instanceOf(Element),
	data: PropTypes.shape({
		upcomingPayPlanAssignment: PropTypes.shape({
			acknowledgementStatus: PropTypes.arrayOf(
				PropTypes.shape({
					status: PropTypes.string,
				})
			),
		}),
	}),
	handleClose: PropTypes.func.isRequired,
};

export default function AssignmentMessages(props) {
	const { status = STATUS_INPROGRESS, statusDate = null, data = null } = props;

	const date = Boolean(statusDate) ? moment(statusDate)?.format(DATE_FORMAT.monthAbbrDatePadded)?.toString() : "";

	const [anchorEl, setAnchorEl] = useState(null);
	const descriptionRef = useRef(null);

	const handleKeyPress = (event) => {
		if (event.key === "Enter" || (event.key === " " && props.status === STATUS_INPROGRESS)) {
			handleClick();
		} else if (event.key === "Escape") {
			event.preventDefault();
			handleClose();
		}
	};

	const handleClick = () => {
		setAnchorEl(descriptionRef.current);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;

	const { icon, message } = STATUS_MAP.get(status) ?? {};

	const clickHandler = inProgressWithData(status, data, handleClick);

	return (
		<Paper ref={descriptionRef} elevation={0} className="!bg-transparent">
			<Box className="!flex font-bold relative">
				<span className="!text-default">{date}</span>
				<img src={icon} className="mx-2" />
				<span
					onClick={clickHandler}
					onKeyDown={handleKeyPress}
					className={clsx({
						"!cursor-pointer !text-primary": status === STATUS_INPROGRESS && data,
						"!cursor-default !text-default": status !== STATUS_INPROGRESS || !data,
					})}
					tabIndex={0}
				>
					{message ?? ""}
				</span>
				{props.isVisibleDTL && (
					<SendEmailButton key="send-email" text="Resend" disabled={props.showRowFeedback} onClick={props.sendEmailMethod} />
				)}
			</Box>
			{data && (
				<MessagePopover
					id={id}
					open={open}
					anchorEl={anchorEl}
					handleClose={handleClose}
					data={props.data}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "left",
					}}
				/>
			)}
		</Paper>
	);
}
AssignmentMessages.propTypes = {
	status: PropTypes.string,
	statusDate: PropTypes.string,
	data: PropTypes.shape({
		upcomingPayPlanAssignment: PropTypes.shape({
			acknowledgementStatus: PropTypes.arrayOf(
				PropTypes.shape({
					status: PropTypes.string,
				})
			),
		}),
	}),
	isVisibleDTL: PropTypes.bool,
	disableSendEmail: PropTypes.bool,
	showRowFeedback: PropTypes.bool,
	sendEmailMethod: PropTypes.func,
	acknowledgementDate: PropTypes.string,
};
